/**
 * CollaborationBar.jsx
 *
 * Additive collaboration UI rendered ABOVE the editor content area.
 * Shows:
 *  - Active collaborator avatars with initials + hover tooltip
 *  - Typing indicators ("✏ Meghana is typing...")
 *  - Connection status badge (🟢 / 🟡 / 🔴)
 *  - Join / leave toast notifications (auto-dismiss, spam-proof)
 *
 * This component is PURELY additive – it does not modify any existing props
 * or layouts. It is rendered by ContentEditor only when collab is active.
 */

import React, { useEffect, useRef, useState } from 'react';
import './CollaborationBar.css';

// ── Typing indicator hook ─────────────────────────────────────────────────────
// Each user broadcasts an "isTyping" flag via awareness. We suppress it after
// 2.5 s of inactivity to avoid WebSocket spam.
export function useTypingIndicator(provider) {
    const typingTimerRef = useRef(null);

    const notifyTyping = () => {
        if (!provider?.awareness) return;
        provider.awareness.setLocalStateField('isTyping', true);

        if (typingTimerRef.current) clearTimeout(typingTimerRef.current);
        typingTimerRef.current = setTimeout(() => {
            provider?.awareness?.setLocalStateField('isTyping', false);
        }, 2500);
    };

    useEffect(() => {
        return () => {
            if (typingTimerRef.current) clearTimeout(typingTimerRef.current);
            provider?.awareness?.setLocalStateField('isTyping', false);
        };
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    return { notifyTyping };
}

// ── Status badge ──────────────────────────────────────────────────────────────
function StatusBadge({ status, onRetry }) {
    const map = {
        connected:    { dot: '#22C55E', label: 'Connected' },
        connecting:   { dot: '#F59E0B', label: 'Connecting…' },
        disconnected: { dot: '#F59E0B', label: 'Reconnecting…' },
        error:        { dot: '#EF4444', label: 'Connection lost – your edits are kept locally' },
        unavailable:  { dot: '#EF4444', label: 'Collaboration unavailable' },
        locked:       { dot: '#F97316', label: 'Section locked' },
    };
    const { dot, label } = map[status] || map.unavailable;

    return (
        <div className="collab-status-badge" title={label}>
            <span className="collab-status-dot" style={{ background: dot }} />
            <span className="collab-status-label">{label}</span>
            {status === 'error' && onRetry && (
                <button type="button" className="collab-retry-btn" onClick={onRetry}
                    style={{ marginLeft: 8, padding: '2px 10px', fontSize: 12, fontWeight: 600, border: '1px solid #EF4444', borderRadius: 6, background: '#FFFFFF', color: '#EF4444', cursor: 'pointer' }}>
                    Retry
                </button>
            )}
        </div>
    );
}

// ── Avatar ────────────────────────────────────────────────────────────────────
function Avatar({ user }) {
    const [showTooltip, setShowTooltip] = useState(false);
    return (
        <div
            className="collab-avatar"
            style={{ background: user.color, borderColor: user.color }}
            onMouseEnter={() => setShowTooltip(true)}
            onMouseLeave={() => setShowTooltip(false)}
        >
            {user.initials}
            {showTooltip && (
                <div className="collab-avatar-tooltip">{user.name}</div>
            )}
        </div>
    );
}

// ── Join/Leave notification (single queue, auto-dismiss) ──────────────────────
let toastIdCounter = 0;
function useJoinLeaveToasts(collaborators) {
    const prevSetRef = useRef(new Set());
    const seenCollaboratorsRef = useRef(new Map());
    const [toasts, setToasts] = useState([]);

    useEffect(() => {
        // Record all current collaborators to the seen map
        collaborators.forEach(c => {
            if (c.clientId && c.name) {
                seenCollaboratorsRef.current.set(c.clientId, c.name);
            }
        });

        const currentIds = new Set(collaborators.map(c => c.clientId));

        // Joined
        collaborators.forEach(c => {
            if (!prevSetRef.current.has(c.clientId)) {
                const id = ++toastIdCounter;
                const msg = `${c.name} joined the document.`;
                setToasts(prev => [...prev.slice(-2), { id, msg, type: 'join' }]); // cap at 3 toasts
                setTimeout(() => setToasts(prev => prev.filter(t => t.id !== id)), 3500);
            }
        });

        // Left
        prevSetRef.current.forEach(clientId => {
            if (!currentIds.has(clientId)) {
                const name = seenCollaboratorsRef.current.get(clientId) || 'A collaborator';
                const id = ++toastIdCounter;
                const msg = `${name} left the document.`;
                setToasts(prev => [...prev.slice(-2), { id, msg, type: 'leave' }]);
                setTimeout(() => setToasts(prev => prev.filter(t => t.id !== id)), 3500);
            }
        });

        prevSetRef.current = currentIds;
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [collaborators]);

    return toasts;
}

// ── Main CollaborationBar component ──────────────────────────────────────────
const CollaborationBar = ({ collaborators, status, provider, onRetry }) => {
    const toasts = useJoinLeaveToasts(collaborators);

    // Derive typing users from awareness
    const [typingUsers, setTypingUsers] = useState([]);
    useEffect(() => {
        if (!provider?.awareness) return;

        const update = () => {
            const states = Array.from(provider.awareness.getStates().entries());
            const typing = states
                .filter(([clientId, state]) =>
                    state?.isTyping && state?.user && clientId !== provider.awareness.clientID
                )
                .map(([, state]) => state.user.name);
            setTypingUsers(typing);
        };

        provider.awareness.on('change', update);
        update();
        return () => provider.awareness.off('change', update);
    }, [provider]);

    // Don't render bar at all if collab is unavailable and there are no collaborators
    if (status === 'unavailable' && collaborators.length === 0) {
        return (
            <div className="collab-unavailable-notice">
                Collaboration unavailable. Working in standard editing mode.
                {onRetry && (
                    <button type="button" onClick={onRetry}
                        style={{ marginLeft: 10, padding: '2px 10px', fontSize: 12, fontWeight: 600, border: '1px solid currentColor', borderRadius: 6, background: 'transparent', color: 'inherit', cursor: 'pointer' }}>
                        Retry
                    </button>
                )}
            </div>
        );
    }

    return (
        <div className="collab-bar">
            {/* Left: Avatars + Status */}
            <div className="collab-bar-left">
                <span className="collab-icon">👥</span>
                <div className="collab-avatars-row">
                    {collaborators.map(c => (
                        <Avatar key={c.clientId} user={c} />
                    ))}
                </div>
                <span className="collab-count">
                    {collaborators.length + 1} active collaborator{(collaborators.length + 1) > 1 ? 's' : ''}
                </span>
            </div>

            {/* Center: Activity / Typing status */}
            <div className="collab-typing-area">
                {typingUsers.length > 0 ? (
                    <span className="collab-typing-text">
                        ✏️ <strong>{typingUsers.join(', ')}</strong> {typingUsers.length > 1 ? 'are' : 'is'} editing this section
                    </span>
                ) : (
                    <span className="collab-typing-text collab-typing-text--idle">
                        ✏️ No active edits from others
                    </span>
                )}
            </div>

            {/* Right: Connection status */}
            <div className="collab-bar-right">
                <StatusBadge status={status} onRetry={onRetry} />
            </div>

            {/* Join/Leave Toasts */}
            <div className="collab-toast-stack">
                {toasts.map(t => (
                    <div key={t.id} className={`collab-toast collab-toast--${t.type}`}>
                        {t.type === 'join' ? '👋' : '👋'} {t.msg}
                    </div>
                ))}
            </div>
        </div>
    );
};

export default CollaborationBar;
